'use client';

/**
 * PricingBenchmarksExplorerCard — static "Benchmarks" row (mode11
 * explorer), a peer of favourites_explorer/custom_products_explorer.
 *
 * Unlike those two, this row carries no tree and no self-fetch of its
 * own — it is always present (the pricing benchmark is a platform
 * fact, not something the buyer earns by saving/wishing). Click sets
 * sel:benchmarks, swapping the canvas to the catalogue-wide L/M/H
 * ladder (pricing_benchmark_ladder).
 *
 * @see klyde_edge/ui_config/composition/cards/search.py — content_key="pricing_benchmarks_explorer"
 * @see ../../../canvas/cards/products/pricing-benchmark-ladder-card.tsx — the canvas twin
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain explorer
 */

import { useMemo } from 'react';
import { LineChart } from 'lucide-react';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useCardExport } from '@/lib/design/card-export';
import { treeDepth as depthRamp, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

export function PricingBenchmarksExplorerCard() {
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const isActive = useLayoutStore((s) => s.explorerSelection) === 'benchmarks';

  useCardExport(useMemo(() => ({
    label: 'Benchmarks',
    content_key: 'pricing_benchmarks_explorer',
    surface: 'explorer' as const,
    status: 'loaded' as const,
    query_key: null,
    text: 'Benchmarks',
    json: null,
  }), []));

  // One row style: this shell deliberately mirrors ProductTierRow's
  // (favourites_explorer/custom_products_explorer's shared primitive)
  // outer card + inner row exactly — same icon size, padding, radius,
  // chevron-slot spacer (so icons share one left edge even though this
  // row has no chevron of its own), and label typography. Benchmarks
  // has no children/count, so those two pieces are simply omitted
  // rather than given a different shape.
  return (
    <button
      type="button"
      onClick={() => setExplorerSelection('benchmarks')}
      style={{ fontSize: surfaceBase.canvas }}
      className={cn(
        'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-colors text-left',
        isActive ? 'border-primary/30 bg-primary/5' : 'border-border bg-card hover:bg-accent/30',
      )}
    >
      <span className="w-4 shrink-0" />
      <LineChart className="h-3.5 w-3.5 shrink-0" />
      <span className={cn(depthRamp[0], 'truncate flex-1', isActive && 'text-primary font-medium')}>
        Benchmarks
      </span>
    </button>
  );
}
